/**
 * Exit Status Helper Utility
 * Determines employee exit status based on exit-related fields
 */

/**
 * Get exit status for an employee
 * @param {Object} employee - Employee object with exit-related fields
 * @returns {Object} - { status, label, color, bgColor, description }
 */
export const getExitStatus = (employee) => {
    if (!employee) {
        return {
            status: 'active',
            label: 'Active',
            color: 'text-green-700 dark:text-green-400',
            bgColor: 'bg-green-100 dark:bg-green-900/30',
            borderColor: 'border-green-200 dark:border-green-800',
            description: 'Currently employed'
        };
    }

    // Check if employee has completed exit process
    if (employee.exit_completed_at || employee.employment_status === 'exited') {
        return {
            status: 'exited',
            label: 'Exited',
            color: 'text-gray-700 dark:text-gray-400',
            bgColor: 'bg-gray-100 dark:bg-gray-900/30',
            borderColor: 'border-gray-200 dark:border-gray-800',
            description: `Exited on ${employee.exit_completed_at ? new Date(employee.exit_completed_at).toLocaleDateString() : 'N/A'}`
        };
    }

    // Check if exit process has been initiated (notice period)
    if (employee.exit_initiated_at || employee.employment_status === 'notice_period') {
        return {
            status: 'notice_period',
            label: 'Notice Period',
            color: 'text-orange-700 dark:text-orange-400',
            bgColor: 'bg-orange-100 dark:bg-orange-900/30',
            borderColor: 'border-orange-200 dark:border-orange-800',
            description: `Serving notice period since ${employee.exit_initiated_at ? new Date(employee.exit_initiated_at).toLocaleDateString() : 'N/A'}`
        };
    }

    // Check other employment statuses
    if (employee.employment_status === 'terminated') {
        return {
            status: 'terminated',
            label: 'Terminated',
            color: 'text-red-700 dark:text-red-400',
            bgColor: 'bg-red-100 dark:bg-red-900/30',
            borderColor: 'border-red-200 dark:border-red-800',
            description: 'Employment terminated'
        };
    }

    if (employee.employment_status === 'resigned') {
        return {
            status: 'resigned',
            label: 'Resigned',
            color: 'text-purple-700 dark:text-purple-400',
            bgColor: 'bg-purple-100 dark:bg-purple-900/30',
            borderColor: 'border-purple-200 dark:border-purple-800',
            description: 'Resigned from position'
        };
    }

    if (employee.employment_status === 'on-leave') {
        return {
            status: 'on-leave',
            label: 'On Leave',
            color: 'text-yellow-700 dark:text-yellow-400',
            bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
            borderColor: 'border-yellow-200 dark:border-yellow-800',
            description: 'Currently on leave'
        };
    }

    // Default: Active
    return {
        status: 'active',
        label: 'Active',
        color: 'text-green-700 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        borderColor: 'border-green-200 dark:border-green-800',
        description: 'Currently employed'
    };
};

/**
 * Get exit status badge component props
 * @param {Object} employee - Employee object
 * @param {string} size - 'sm', 'md', 'lg'
 * @returns {Object} - Props for badge component
 */
export const getExitStatusBadge = (employee, size = 'md') => {
    const exitStatus = getExitStatus(employee);

    const sizeClasses = {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-3 py-1 text-sm',
        lg: 'px-4 py-2 text-base'
    };

    return {
        ...exitStatus,
        className: `inline-flex items-center rounded-full font-medium ${sizeClasses[size]} ${exitStatus.bgColor} ${exitStatus.color} border ${exitStatus.borderColor}`
    };
};

/**
 * Check if employee is in exit process
 * @param {Object} employee - Employee object
 * @returns {boolean}
 */
export const isInExitProcess = (employee) => {
    if (!employee) return false;
    return !!(employee.exit_initiated_at && !employee.exit_completed_at);
};

/**
 * Check if employee has exited
 * @param {Object} employee - Employee object
 * @returns {boolean}
 */
export const hasExited = (employee) => {
    if (!employee) return false;
    return !!(employee.exit_completed_at || employee.employment_status === 'exited');
};

/**
 * Check if employee's exit date has passed but exit not completed
 * @param {Object} employee - Employee object
 * @param {Object} exitRequest - Exit request object (optional)
 * @returns {Object} { passed: boolean, exitDate: Date|null, daysPassed: number }
 */
export const isExitDatePassed = (employee, exitRequest = null) => {
    if (!employee) return { passed: false, exitDate: null, daysPassed: 0 };

    // Employee must be in exit process (notice period) and NOT already exited
    if (!isInExitProcess(employee) || hasExited(employee)) {
        return { passed: false, exitDate: null, daysPassed: 0 };
    }

    // Get exit date from exit request or employee record
    const exitDateStr = exitRequest?.approved_exit_date ||
                        exitRequest?.requested_exit_date ||
                        employee.exit_date ||
                        employee.exitDate ||
                        employee.last_working_day;

    if (!exitDateStr) return { passed: false, exitDate: null, daysPassed: 0 };

    const exitDate = new Date(exitDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for accurate day comparison
    exitDate.setHours(0, 0, 0, 0);

    const diffTime = today - exitDate;
    const daysPassed = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return {
        passed: daysPassed >= 0,  // FIX BUG-001: Changed > to >= to show banner on actual exit date
        exitDate,
        daysPassed: Math.max(0, daysPassed)
    };
};

/**
 * Get days remaining in notice period
 * @param {Object} employee - Employee object with exit request data
 * @param {Object} exitRequest - Exit request object
 * @returns {number|null} - Days remaining (negative if passed), or null if no exit date
 */
export const getDaysRemainingInNoticePeriod = (employee, exitRequest = null) => {
    if (!isInExitProcess(employee) && !exitRequest) return null;

    // FIX: Check exitRequest fields first, then fallback to employee fields
    const exitDateStr = exitRequest?.approved_exit_date ||
                    exitRequest?.requested_exit_date ||
                    exitRequest?.approvedExitDate ||
                    exitRequest?.requestedExitDate ||
                    employee?.exit_date ||
                    employee?.exitDate ||
                    employee?.last_working_day;

    if (!exitDateStr) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to midnight for accurate day calculation
    const exit = new Date(exitDateStr);
    exit.setHours(0, 0, 0, 0);

    const diffTime = exit - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Return actual value - negative means exit date has passed
    return diffDays;
};

/**
 * Get exit status description with details
 * @param {Object} employee - Employee object
 * @param {Object} exitRequest - Exit request object (optional)
 * @returns {string} - Detailed status description
 */
export const getExitStatusDescription = (employee, exitRequest = null) => {
    const status = getExitStatus(employee);

    if (status.status === 'notice_period' && exitRequest) {
        const daysRemaining = getDaysRemainingInNoticePeriod(employee, exitRequest);
        if (daysRemaining !== null) {
            const exitDate = exitRequest.approved_exit_date || exitRequest.requested_exit_date;
            return `Serving notice period • Last working day: ${new Date(exitDate).toLocaleDateString()} • ${daysRemaining} days remaining`;
        }
    }

    return status.description;
};

/**
 * Get exit status icon
 * @param {string} status - Status string
 * @returns {string} - Icon character/emoji
 */
export const getExitStatusIcon = (status) => {
    const icons = {
        active: '✓',
        notice_period: '⏰',
        exited: '→',
        terminated: '✗',
        resigned: '📝',
        'on-leave': '🏖️'
    };

    return icons[status] || '•';
};

const exitStatusHelper = {
    getExitStatus,
    getExitStatusBadge,
    isInExitProcess,
    hasExited,
    isExitDatePassed,
    getDaysRemainingInNoticePeriod,
    getExitStatusDescription,
    getExitStatusIcon
};

export default exitStatusHelper;
